<script setup>
import { useField } from 'vee-validate'
import { useTemplateRef, watch } from 'vue'
import { useRouter } from 'vue-router'

import ImoSpinner from '@/components/loading/ImoSpinner.vue'
import { useFormatTools } from '@/composables/useFormatTools'
import { useSearchDropdown } from '@/composables/useSearchDropdown'
import Wallets from '@/services/models/Wallets.js'

const { formatMoney } = useFormatTools()

const emit = defineEmits(['input', 'selectData'])

const props = defineProps({
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md', 'lg'].includes(value),
  },
  name: {
    type: String,
    required: true,
  },
  label: {
    type: String,
    default: 'Carteira:',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  showWalletsByStatus: {
    type: [Boolean, null],
    default: null,
  },
})

const router = useRouter()

const inputRef = useTemplateRef('inputRef')
const dropdownRef = useTemplateRef('dropdownRef')
const ulRef = useTemplateRef('ulRef')

const model = defineModel({ type: String })

const { value, errorMessage, handleReset } = useField(() => props.name, undefined, {
  syncVModel: true,
  validateOnValueUpdate: false,
})

// Hook do dropdown
const {
  searchQuery,
  results,
  isDropdownVisible,
  isLoading,
  isFinished,
  activeIndex,
  isKeyboardNavigation,
  openDropdown,
  closeDropdown,
  selectResult,
  cleanAll,
  handleKeyDown,
  handleScroll,
  handleMouseEnterDropdown,
  handleMouseLeaveDropdown,
  handleMouseEnterItem,
} = useSearchDropdown({
  fetchService: url => Wallets.list(url),
  readService: id => Wallets.read(id),
  buildSearchUrl: searchValue => {
    const params = new URLSearchParams({ search: searchValue })
    if (props.showWalletsByStatus !== null && props.showWalletsByStatus !== undefined) {
      params.append('active', props.showWalletsByStatus)
    }
    return `?${params.toString()}`
  },
  onSelect: item => {
    handleReset()
    emit('selectData', item)
    value.value = item.id
    searchQuery.value = item.name
  },
  inputRef,
  dropdownRef,
  ulRef,
  initialValue: model,
  debounceDelay: 300,
})

// Função para limpar tudo incluindo vee-validate
const handleCleanAll = () => {
  value.value = ''
  handleReset()
  cleanAll()
}

// Override da função openDropdown para incluir reset de erros
const handleOpenDropdown = () => {
  if (errorMessage.value) {
    handleReset()
  }
  openDropdown()
}

const newWallet = () => {
  router.push({ name: 'Carteiras', params: { new: 'true' } })
}

const mapInterest = interest => {
  if (!interest || interest.value == null) return ''

  const formatted = formatMoney(interest.value)
  const interestMap = {
    df: `R$ ${formatted} a.d`,
    dp: `${formatted}% a.d`,
    mp: `${formatted}% a.m`,
  }

  return interestMap[interest.charge_type] || ''
}

// Sincronizar v-model externo com searchQuery
watch(model, newValue => {
  if (!newValue) {
    searchQuery.value = ''
  }
})
</script>

<template>
  <div class="relative flex flex-col gap-1">
    <label
      class="text-neutral-60"
      :class="{
        'text-base': size === 'lg',
        'text-sm': size === 'md',
        'text-xs': size === 'sm',
      }"
      :for="$attrs.id"
    >
      {{ label }}
    </label>
    <div class="relative w-full">
      <input
        class="placeholder:text-neutral-40 w-full rounded-lg pr-16"
        :class="{
          error: errorMessage || (!results.length && !isLoading),
          input: !errorMessage && (results.length || isLoading),
          'h-[38px]': size === 'md',
          'h-[44px]': size === 'lg',
          'h-[36px]': size === 'sm',
          'border-neutral-30': !value,
          'border-neutral-40': value,
        }"
        id="search-input"
        type="text"
        v-model="searchQuery"
        @focus="handleOpenDropdown"
        @blur="closeDropdown"
        @keydown="handleKeyDown"
        placeholder="Digite para buscar..."
        ref="inputRef"
        autocomplete="off"
      />

      <!-- Ícone de limpar/erro (X) ou spinner de loading -->
      <div v-if="searchQuery" class="absolute top-1/2 right-3 -translate-y-1/2">
        <ImoSpinner v-if="isLoading" :size="20" variant="tertiary" />
        <ImoIcon
          v-else
          @click="handleCleanAll"
          class="cursor-pointer"
          :class="{
            'text-error-60': errorMessage || (!results.length && !isLoading),
            'text-gray-400 hover:text-gray-600': !errorMessage && (results.length || isLoading),
          }"
          name="close"
        />
      </div>

      <!-- Ícone de seta (arrow-down) - aparece sempre que não há erro nem texto -->
      <ImoIcon
        v-if="!searchQuery"
        class="text-neutral-40 pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 transition-transform duration-200"
        :class="{
          'rotate-180': isDropdownVisible,
        }"
        name="arrow-down-02"
        :size="24"
      />
    </div>
    <input id="wallet-input-id" type="text" v-model="value" class="hidden" />

    <!-- Dropdown -->
    <div
      v-if="isDropdownVisible"
      ref="dropdownRef"
      class="border-neutral-20 absolute top-17 z-40 w-full rounded-lg border bg-white p-1 shadow-lg"
      @mouseenter="handleMouseEnterDropdown"
      @mouseleave="handleMouseLeaveDropdown"
    >
      <div class="flex flex-col gap-0.5 rounded-lg">
        <ul
          v-if="results.length"
          ref="ulRef"
          class="dropdown-list scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-20 max-h-[215px] overflow-y-auto"
          @scroll="handleScroll"
        >
          <li
            v-for="(item, index) in results"
            :key="index"
            @mousedown="selectResult(item)"
            @mouseenter="handleMouseEnterItem(index)"
            class="flex cursor-pointer flex-col gap-0.5 rounded-lg py-2 pr-5 pl-4"
            :class="{
              'bg-neutral-20': index === activeIndex,
              'hover:bg-neutral-20': index !== activeIndex && !isKeyboardNavigation,
            }"
          >
            <span class="text-neutral-90 text-sm font-normal">{{ item.name }} </span>
            <span class="text-neutral-60 text-xs font-normal">
              D. Tolerância: {{ item.tolerance_days }}
              <span v-if="item.configurations?.fine">
                - Multa:
                {{
                  item.configurations.fine.charge_type === 'p'
                    ? `${formatMoney(item.configurations.fine.value)}%`
                    : `R$ ${formatMoney(item.configurations.fine.value)}`
                }}
              </span>
              <span v-if="item.configurations?.interest">
                - Juros:
                {{ mapInterest(item.configurations.interest) }}
              </span>
            </span>
            <span
              v-if="item.configurations?.discounts?.length"
              class="text-neutral-60 text-xs font-normal"
            >
              Descontos:
              <span
                v-for="discount in item.configurations.discounts"
                :key="discount.id"
                class="border-neutral-60 border-r px-1 last:border-none"
              >
                {{ discount.days }} Dia(s):
                {{
                  discount.charge_type === 'p'
                    ? `${formatMoney(discount.value)}%`
                    : `R$ ${formatMoney(discount.value)}`
                }}
              </span>
            </span>
          </li>
          <!-- Spinner no final da lista quando carrega mais itens -->
          <li v-if="isLoading && !isFinished" class="flex justify-center py-3">
            <ImoSpinner :size="20" variant="tertiary" />
          </li>
        </ul>
        <div v-else-if="!isLoading" class="flex flex-col gap-6 rounded-lg py-2 pr-5 pl-4">
          <span class="text-error-60 text-center text-xs font-normal">
            Carteira não encontrada. Verifique os dados digitados ou cadastre uma nova carteira.
          </span>
        </div>
        <ImoTextButton
          text="Cadastrar nova carteira"
          size="sm"
          icon="add"
          iconPosition="left"
          @mousedown="newWallet"
        />
      </div>
    </div>

    <p
      v-if="!results.length && !isLoading && !isDropdownVisible && !errorMessage"
      class="text-error-60 text-xs"
    >
      Não localizado. Verifique os dados digitados ou
      <strong class="cursor-pointer" @click="newWallet"> cadastre um novo </strong>.
    </p>

    <span class="text-error-60 text-xs" :id="`contact-${$attrs.id}-errors`">
      {{ errorMessage }}
    </span>
  </div>
</template>

<style scoped>
@reference "@/assets/style/main.css";

.input {
  @apply disabled:bg-neutral-10 disabled:border-neutral-30 disabled:text-neutral-40;
}

.input:focus-visible {
  @apply border-[#4c94ff73];
  box-shadow: 0px 0px 0px 3px #4c94ff73;
}

.error {
  @apply bg-error-10 border-error-60 text-error-60;
  box-shadow: 0px 0px 0px 3px #f008024d;
}
</style>
